import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { Row, Col } from 'react-bootstrap';
import { AppLayout } from '@/components/layout';
import {
  ReleaseSuccessHero,
  ReleaseCodePanel,
  ReleaseStepGuide,
} from '@/components/release/index';
import { ROUTES } from '@/constants/routes';
import type { ReleaseGuideStep } from '@/types/release.types';
import type { ConfirmationPageState } from '@/types/payment.types';
import { useSessionStore } from '@/store/useSessionStore';
import { copyToClipboard } from '@/utils/clipboard';
import toast from 'react-hot-toast';
import { shareOnWhatsApp } from '@/utils/share';

// TODO: no kiosk-hardware/location service is wired up yet — tray status,
// step guide, and terminal location stay mocked until those exist.

const RELEASE_STEPS: ReleaseGuideStep[] = [
  {
    id: 'walk',
    title: 'Walk to the touch display',
    description: 'Go to near Print Buddie and look for the screen.',
  },
  {
    id: 'tap',
    title: "Tap 'Enter Print OTP Code'",
    description: 'Type the 6-digit code shown above on the Print Buddies keypad. Your document will appear on the screen.',
  },
  {
    id: 'type',
    title: 'Print releases instantly',
    description: 'Check your document, then tap Print to print your pages and simply collect your printed pages.'
  },
];

// OTP is valid for 24 hours from the successful payment
const TOTAL_SECONDS = 24 * 60 * 60;

function useCountdown(expiresAt: string | null) {
  const target = expiresAt ? new Date(expiresAt).getTime() : null;
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    if (target == null) return;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [target]);

  const secondsRemaining = target == null ? 0 : Math.max(0, Math.floor((target - now) / 1000));
  return { secondsRemaining, isExpired: target != null && secondsRemaining === 0 };
}

function formatRemaining(total: number) {
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  return `${h}h ${String(m).padStart(2, '0')}m ${String(s).padStart(2, '0')}s`;
}

export function OtpSuccessPage() {
  const location = useLocation();
  const navigate = useNavigate();

  // Router state first (fresh from payment), else the persisted copy (refresh)
  const routeState = location.state as ConfirmationPageState | null;
  const storedConfirmation = useSessionStore((s) => s.confirmation);
  const state = routeState ?? storedConfirmation;

  const kioskToken = useSessionStore((s) => s.kioskToken);
  const resetSession = useSessionStore((s) => s.resetSession);

  // All hooks stay above the early return below
  const { secondsRemaining, isExpired } = useCountdown(state?.otpExpiresAt ?? null);

  const handleDone = () => {
    resetSession(); // keeps kiosk + kioskToken, clears confirmation
    navigate(kioskToken ? `${ROUTES.upload}?token=${encodeURIComponent(kioskToken)}` : ROUTES.upload);
  };

  // No state means the user landed here directly rather than via a real
  // payment — nothing to release.
  if (!state) return <Navigate to={ROUTES.upload} replace />;

  const handleCopyCode = async () => {
    if (!state.otp || isExpired) return;
    const success = await copyToClipboard(state.otp);
    if (success) {
      toast.success('Code copied');
    } else {
      toast.error('Could not copy code — please note it down manually');
    }
  };

  const orderId = `KP-${state.printRequestId}`;

  const handleShareWhatsApp = () => {
    if (!state.otp || isExpired) return;
    shareOnWhatsApp({
      otp: state.otp,
      orderId,
      fileName: state.fileName,
      expiresAt: state.otpExpiresAt,
    });
  };

  const codePanel = (
    <>
      {isExpired ? (
        <div className="alert alert-danger" role="alert">
          This code has expired. Please contact the kiosk helpline.
        </div>
      ) : (
        <ReleaseCodePanel
          code={state.otp ?? '------'}
          secondsRemaining={secondsRemaining}
          totalSeconds={TOTAL_SECONDS}
          onCopy={handleCopyCode}
        />
      )}
      {!isExpired && state.otpExpiresAt && (
        <p className="text-body-sm text-on-surface-variant text-center mt-2 mb-0">
          Valid for {formatRemaining(secondsRemaining)} (until{' '}
          {new Date(state.otpExpiresAt).toLocaleString()})
        </p>
      )}
      <button
        type="button"
        onClick={handleShareWhatsApp}
        disabled={!state.otp || isExpired}
        className="btn btn-success tap-target active-press w-100 d-flex align-items-center justify-content-center gap-2 text-label-lg mt-3"
        style={{ height: 52 }}
      >
        <i className="bi bi-whatsapp" />
        <span>Share code on WhatsApp</span>
      </button>
    </>
  );

  const stepGuide = <ReleaseStepGuide title="How to release at Print Buddie " steps={RELEASE_STEPS} isTerminalOnline />;

  return (
    <AppLayout title="Release Print" currentStep={4}>
      <ReleaseSuccessHero
        title="Payment Successful & Print Queued"
        description="Your document is queued and ready for instant physical release."
        statusLabel="Kiosk is locked & awaiting release"
        orderId={orderId}
      />

      <div className="d-lg-none">
        {codePanel}
        {stepGuide}
      </div>

      <Row className="g-4 d-none d-lg-flex">
        <Col lg={7}>{codePanel}</Col>
        <Col lg={5}>{stepGuide}</Col>
      </Row>

      <div className="d-flex flex-column align-items-center text-center mt-4">
        <button
          type="button"
          onClick={handleDone}
          className="btn btn-outline-primary tap-target active-press d-flex align-items-center gap-2 text-label-lg px-4"
          style={{ height: 52 }}
        >
          <i className="bi bi-plus-circle" />
          <span>Done / Print Another File</span>
        </button>

        <p className="text-body-sm text-on-surface-variant mt-3 mb-0 d-flex align-items-center gap-2">
          <i className="bi bi-headset" />
          Kiosk Helpline: +91 800-KIOSK-01 (Toll Free)
        </p>
      </div>
    </AppLayout>
  );
}